// ============================================================================
// EhrSuggestions — Build 1C (Gate EHR suggestion review)
//
// A focused clone of PacketSuggestions for FHIR/EHR candidates (Build 1A). It
// reuses PatientStepPHI's source-agnostic `fieldsRef` adapter and the OCR
// fieldKey namespace, so confirmed values flow into the SAME form state.
//
// Faithful OCR mirror:
//   - candidates visually PREFILL their matching field on load, isEmpty-guarded
//     (never overwrite a manual or already-applied OCR value)
//   - each prefilled field is PENDING until the GP Uses (ACCEPT/EDIT) or Dismisses
//   - pending appliable fields block Continue (combined with OCR pending in the host)
//   - candidate values come ONLY from the gp-only callable; nothing here writes
//     final referral PHI — submit still uses confirmed form state only.
//
// EHR candidates are one-per-fieldKey (Build 1A maps a single primary value per
// field), so there is no cross-document conflict UI.
// ============================================================================

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Sparkles, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import { listPacketEhrCandidates, reviewPacketEhrCandidate, EHR_REVIEW_ACTION } from '../../core/ehrApi.js';
import { SECTION_FIELDS } from './PacketSuggestions.jsx';
import { CANDIDATE_FIELD_LABELS } from '../../core/store.packets.js';
import { deriveUsedCandidateRefs } from '../../core/ehrCandidateProvenance.js';

const SECTION_LABEL = { patient: 'patient', contact: 'contact', insurance: 'insurance' };

// Captured but with no matching form field — shown, not applied, never blocks.
const NOT_APPLIED_FIELDS = new Set(['insurance.planName']);

// EHR import statuses that are still actionable (awaiting GP review).
const EHR_ACTIONABLE_STATUSES = ['SUGGESTED', 'LOW_CONFIDENCE'];

const applyValue = (c) => (c.normalizedValue != null && c.normalizedValue !== '' ? c.normalizedValue : c.value) || '';
const displayValue = (c) => (c.reviewedValue != null ? c.reviewedValue : (c.normalizedValue != null ? c.normalizedValue : c.value)) || '';

const labelFor = (fieldKey) => CANDIDATE_FIELD_LABELS[fieldKey] || fieldKey;
const sourceLine = (s) => {
  const label = s && s.sourceLabel ? s.sourceLabel : 'EHR';
  const rt = s && s.fhirResourceType ? ` · ${s.fhirResourceType}` : '';
  return `${label}${rt}`;
};

const Ctx = createContext(null);

// ── Provider ─────────────────────────────────────────────────────────────────

// Build 2N — `autoFill` (default true): EHR-started packets auto-load EHR
// patient/admin/insurance values into editable draft fields WITHOUT a per-field
// Use/Reject step. Provenance stays ("Used from EHR · source"); the GP can still
// edit/clear; the final Gate Submit is the confirmation. When false, the original
// pending/confirm review flow is used. (EHR candidates only ever exist on
// EHR-started packets, so the default matches the trigger.)
export const EhrSuggestionsProvider = ({ packetId, fieldsRef, onPendingChange, autoFill = true, onLoaded, provenanceRef, children }) => {
  const [loading, setLoading] = useState(Boolean(packetId));
  const [error, setError] = useState(null);
  const [fieldState, setFieldState] = useState({}); // fieldKey → { candidateId, status, baseline?, noField?, value?, sourceLabel, fhirResourceType, reviewStatus }
  const [rowBusy, setRowBusy] = useState({});
  const [sectionBusy, setSectionBusy] = useState({});
  const [count, setCount] = useState(0);

  // Notify host of unresolved PENDING appliable count (drives Continue blocker).
  useEffect(() => {
    const n = Object.values(fieldState).filter((s) => s.status === 'pending' && !s.noField).length;
    if (typeof onPendingChange === 'function') onPendingChange(n);
  }, [fieldState, onPendingChange]);

  const applyPrefill = useCallback((list) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    const fs = {};

    // One candidate per fieldKey (highest confidence wins; EHR is deterministic).
    const byField = {};
    for (const c of list) {
      if (!EHR_ACTIONABLE_STATUSES.includes(c.status)) continue;
      const prev = byField[c.fieldKey];
      if (!prev || (c.confidence || 0) > (prev.confidence || 0)) byField[c.fieldKey] = c;
    }

    for (const fk of Object.keys(byField)) {
      const c = byField[fk];
      // In auto-fill mode the field is loaded as an editable draft (no confirm
      // step); reviewStatus ACCEPTED drives the "Used from EHR · source" provenance.
      const meta = { sourceLabel: c.sourceLabel, fhirResourceType: c.fhirResourceType, reviewStatus: autoFill ? 'ACCEPTED' : c.status };

      if (NOT_APPLIED_FIELDS.has(fk)) {
        // No matching form field. Auto-fill: passive provenance note (non-blocking).
        // Manual: keep the acknowledge (pending, non-blocking) row.
        fs[fk] = autoFill
          ? { candidateId: c.candidateId, status: 'confirmed', noField: true, value: displayValue(c), ...meta }
          : { candidateId: c.candidateId, status: 'pending', noField: true, value: displayValue(c), ...meta };
        continue;
      }
      if (!f) continue;
      if (!f.isEmpty(fk)) continue; // manual / already-applied OCR value wins — never overwrite

      const formVal = f.transform(fk, applyValue(c));
      f.setRaw(fk, formVal);
      // Auto-fill: confirmed (no pending, no server review, no Continue block) —
      // the value is a draft the GP can edit; Gate Submit is the confirmation.
      // Manual: pending until the GP Uses/Dismisses it.
      fs[fk] = autoFill
        ? { candidateId: c.candidateId, status: 'confirmed', baseline: formVal, ...meta }
        : { candidateId: c.candidateId, status: 'pending', baseline: formVal, ...meta };
    }

    // Already-reviewed candidates from a prior session → show confirmed (no prefill).
    for (const c of list) {
      if (fs[c.fieldKey]) continue;
      if (c.status === 'ACCEPTED' || c.status === 'EDITED') {
        fs[c.fieldKey] = { candidateId: c.candidateId, status: 'confirmed', sourceLabel: c.sourceLabel, fhirResourceType: c.fhirResourceType, reviewStatus: c.status };
      }
      // REJECTED → ignore (hidden)
    }

    setFieldState(fs);
  }, [fieldsRef, autoFill]);

  // Build 2O — fire onLoaded after EHR prefill resolves (success or failure). The
  // OCR provider waits for this so EHR (primary) fills patient/admin/insurance
  // fields BEFORE OCR runs; OCR's existing isEmpty guard then never overwrites an
  // EHR-filled field. Always fires (even with no packetId) so OCR is never stuck.
  const signalLoaded = () => { if (typeof onLoaded === 'function') onLoaded(); };
  const load = useCallback(() => {
    if (!packetId) { setLoading(false); signalLoaded(); return; }
    setLoading(true);
    setError(null);
    listPacketEhrCandidates({ packetId })
      .then((res) => {
        const list = Array.isArray(res?.candidates) ? res.candidates : [];
        setCount(list.length);
        applyPrefill(list);
      })
      .catch((err) => {
        console.error('[EhrSuggestions] list failed:', err?.message);
        setError('Could not load EHR suggestions. You can continue manually.');
      })
      .finally(() => { setLoading(false); signalLoaded(); });
  }, [packetId, applyPrefill, onLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const callReview = async (candidateId, action, editedValue) => {
    const res = await reviewPacketEhrCandidate({ packetId, candidateId, action, editedValue });
    return res?.status || action;
  };

  const setStatus = (fk, status, reviewStatus) =>
    setFieldState((prev) => ({ ...prev, [fk]: { ...prev[fk], status, reviewStatus } }));

  // ACCEPT if unchanged from the prefilled baseline, EDIT if the GP changed it.
  const confirmOne = async (fk, s) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    let editedVal;
    if (!s.noField && f) {
      const cur = f.get(fk);
      if (cur !== s.baseline) editedVal = cur;
    }
    if (editedVal !== undefined) {
      await callReview(s.candidateId, EHR_REVIEW_ACTION.EDIT, editedVal);
    } else {
      await callReview(s.candidateId, EHR_REVIEW_ACTION.ACCEPT);
    }
    setStatus(fk, 'confirmed', editedVal !== undefined ? 'EDITED' : 'ACCEPTED');
  };

  const dismissOne = async (fk, s) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    await callReview(s.candidateId, EHR_REVIEW_ACTION.REJECT);
    if (!s.noField && f && f.get(fk) === s.baseline) f.clear(fk);
    setStatus(fk, 'dismissed', 'REJECTED');
  };

  const useField = useCallback(async (fk) => {
    const s = fieldState[fk];
    if (!s || s.status !== 'pending') return;
    setRowBusy((b) => ({ ...b, [fk]: true }));
    try { await confirmOne(fk, s); } catch (e) { console.error('[EhrSuggestions] use failed:', e?.message); }
    finally { setRowBusy((b) => ({ ...b, [fk]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissField = useCallback(async (fk) => {
    const s = fieldState[fk];
    if (!s || s.status !== 'pending') return;
    setRowBusy((b) => ({ ...b, [fk]: true }));
    try { await dismissOne(fk, s); } catch (e) { console.error('[EhrSuggestions] dismiss failed:', e?.message); }
    finally { setRowBusy((b) => ({ ...b, [fk]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  const useSection = useCallback(async (section) => {
    const fks = (SECTION_FIELDS[section] || []).filter((fk) => fieldState[fk] && fieldState[fk].status === 'pending');
    if (fks.length === 0) return;
    setSectionBusy((b) => ({ ...b, [section]: true }));
    try { for (const fk of fks) { try { await confirmOne(fk, fieldState[fk]); } catch { /* skip */ } } }
    finally { setSectionBusy((b) => ({ ...b, [section]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissSection = useCallback(async (section) => {
    const fks = (SECTION_FIELDS[section] || []).filter((fk) => fieldState[fk] && fieldState[fk].status === 'pending');
    if (fks.length === 0) return;
    setSectionBusy((b) => ({ ...b, [section]: true }));
    try { for (const fk of fks) { try { await dismissOne(fk, fieldState[fk]); } catch { /* skip */ } } }
    finally { setSectionBusy((b) => ({ ...b, [section]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  const sectionHasPending = useCallback((section) =>
    (SECTION_FIELDS[section] || []).some((fk) => fieldState[fk] && fieldState[fk].status === 'pending'),
  [fieldState]);

  const getFieldValue = useCallback((fk) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    return f ? f.get(fk) : '';
  }, [fieldsRef]);

  // Build 2J P2 — collect the never-PHI EHR candidate usage (refs / field keys /
  // enums / counts only) for the referral audit log. Pure derivation; carries
  // packetId so the audit names the source packet. Exposed to the host
  // (PatientStepPHI) via provenanceRef so the submit path can attach safe
  // provenance WITHOUT reaching into form PHI. Never returns a candidate value.
  const collectUsedCandidateRefs = useCallback(() => {
    const derived = deriveUsedCandidateRefs({ fieldState, getValue: getFieldValue, autoFill });
    return { packetId, ...derived };
  }, [fieldState, getFieldValue, autoFill, packetId]);

  useEffect(() => {
    if (!provenanceRef) return undefined;
    provenanceRef.current = collectUsedCandidateRefs;
    return () => { if (provenanceRef.current === collectUsedCandidateRefs) provenanceRef.current = null; };
  }, [provenanceRef, collectUsedCandidateRefs]);

  const value = {
    packetId, loading, error, fieldState, rowBusy, sectionBusy, count, autoFill,
    useField, dismissField, useSection, dismissSection, sectionHasPending, getFieldValue,
    collectUsedCandidateRefs,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// ── Header note ──────────────────────────────────────────────────────────────

export const EhrSuggestionsNote = () => {
  const ctx = useContext(Ctx);
  if (!ctx || !ctx.packetId) return null;
  if (ctx.loading) {
    return <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading EHR suggestions…</div>;
  }
  if (ctx.error) {
    return <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-700/40 bg-amber-900/20 text-xs text-amber-200"><AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {ctx.error}</div>;
  }
  if (!ctx.count) return null;
  const note = ctx.autoFill
    ? 'EHR values were loaded into the fields below. Review and edit as needed — the referral is created only when you submit.'
    : 'EHR suggestions prefill the fields below but are not patient data until you Use them.';
  return <div className="flex items-center gap-2 text-xs text-sky-300"><Sparkles className="w-3.5 h-3.5" /> {note}</div>;
};

// ── Section-level Use all / Dismiss all ──────────────────────────────────────

export const EhrSectionControls = ({ section }) => {
  const ctx = useContext(Ctx);
  if (!ctx || !ctx.packetId) return null;
  if (!ctx.sectionHasPending(section)) return null;
  const busy = ctx.sectionBusy[section];
  return (
    <div className="mb-2 flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-700/80 text-sky-50">
        <Sparkles className="w-3 h-3" /> Pending EHR — needs confirmation
      </span>
      <button type="button" onClick={() => ctx.useSection(section)} disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Use all {SECTION_LABEL[section]} EHR suggestions
      </button>
      <button type="button" onClick={() => ctx.dismissSection(section)} disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50">
        <X className="w-3.5 h-3.5" /> Dismiss all
      </button>
    </div>
  );
};

// ── Inline per-field confirmation row ────────────────────────────────────────

export const EhrFieldSuggestions = ({ fieldKey }) => {
  const ctx = useContext(Ctx);
  if (!ctx || !ctx.packetId) return null;
  const s = ctx.fieldState[fieldKey];
  if (!s) return null;
  if (s.status === 'dismissed') return null;
  const busy = ctx.rowBusy[fieldKey];

  if (s.status === 'confirmed') {
    const note = s.reviewStatus === 'EDITED' ? 'Used edited value' : 'Used';
    return (
      <div className="mt-1 text-[11px] text-emerald-400 flex items-center gap-1">
        <Check className="w-3 h-3 shrink-0" /> {note} from {sourceLine(s)}
      </div>
    );
  }

  // pending
  const edited = !s.noField && ctx.getFieldValue(fieldKey) !== s.baseline;
  const useLabel = edited ? 'Use edited value' : 'Use';
  return (
    <div className="mt-1.5 rounded-md border border-sky-700/40 bg-sky-950/30 p-2 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Sparkles className="w-3 h-3 text-sky-300 shrink-0" />
        <span className="text-sky-200">{sourceLine(s)}</span>
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-600 text-slate-100">
          {labelFor(fieldKey)} · needs confirmation
        </span>
      </div>
      {s.noField && (
        <div className="mt-0.5 text-slate-300">
          {s.value}<span className="ml-1 text-[11px] text-slate-500">· not applied to this form</span>
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-1.5">
        <button type="button" onClick={() => ctx.useField(fieldKey)} disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {s.noField ? 'Acknowledge' : useLabel}
        </button>
        <button type="button" onClick={() => ctx.dismissField(fieldKey)} disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50">
          <X className="w-3.5 h-3.5" /> Reject
        </button>
      </div>
    </div>
  );
};

export default EhrFieldSuggestions;
